import { ReactElement, useCallback, useState } from 'react';
import { IconButton, MenuItem, Tooltip } from '@mui/material';
import { MoreHoriz as MoreIcon } from '@mui/icons-material';
import { ButtonWithDialog, DropdownProps, Dropdown } from '@localstack/ui';
import { UserMembershipView, UserRole } from '@localstack/types';

import { MembershipActionAvailability, MembershipActionsAvailability } from './membershipActions';

export interface MembershipActionsDropdownProps {
  member: UserMembershipView;
  actions: MembershipActionsAvailability;
  onUpdateRole: (userId: string, role: UserRole) => Promise<void>;
  onRequestEditPermissions: () => void;
  onRemoveMember: (userId: string) => Promise<void>;
}

type ActionTooltipProps = {
  availability: MembershipActionAvailability;
  children: ReactElement;
};

const ActionTooltip = ({ availability, children }: ActionTooltipProps): ReactElement => (
  <Tooltip title={availability.reason ?? ''} placement="left">
    <span>{children}</span>
  </Tooltip>
);

export const MembershipActionsDropdown = ({
  member,
  actions,
  onUpdateRole,
  onRequestEditPermissions,
  onRemoveMember,
}: MembershipActionsDropdownProps): JSX.Element => {
  const [isBusy, setIsBusy] = useState(false);

  const updateRole = useCallback(async (userId: string, role: UserRole) => {
    setIsBusy(true);
    await onUpdateRole(userId, role);
    setIsBusy(false);
  }, []);

  const removeMember = useCallback(async (userId: string) => {
    setIsBusy(true);
    await onRemoveMember(userId);
    setIsBusy(false);
  }, []);

  return (
    <Dropdown
      size="small"
      menuListProps={{ disablePadding: true }}
      renderButton={({ onClick }: DropdownProps) => (
        <ActionTooltip availability={actions.menu}>
          <IconButton onClick={onClick} disabled={!actions.menu.allowed}>
            <MoreIcon />
          </IconButton>
        </ActionTooltip>
      )}
    >
      {member.membership_details.role === UserRole.MEMBER && (
        <ActionTooltip availability={actions.change_role}>
          <ButtonWithDialog
            componentType="MenuItem"
            disabled={isBusy || !actions.change_role.allowed}
            dialogTitle={`Change ${member.user_email} to admin?`}
            dialogContent={`${member.user_email} will be granted admin permissions and full access to this workspace.`}
            confirmButtonText="Confirm"
            onClick={() => updateRole(member.user_id, UserRole.ADMIN)}
          >
            Change to admin&hellip;
          </ButtonWithDialog>
        </ActionTooltip>
      )}
      {member.membership_details.role === UserRole.ADMIN && (
        <ActionTooltip availability={actions.change_role}>
          <ButtonWithDialog
            componentType="MenuItem"
            disabled={isBusy || !actions.change_role.allowed}
            dialogTitle={`Change ${member.user_email} to member?`}
            dialogContent={`${member.user_email} will lose admin permissions and may lose access to some features.`}
            confirmButtonText="Confirm"
            onClick={() => updateRole(member.user_id, UserRole.MEMBER)}
          >
            Change to member&hellip;
          </ButtonWithDialog>
        </ActionTooltip>
      )}
      <ActionTooltip availability={actions.edit_permissions}>
        <MenuItem disabled={isBusy || !actions.edit_permissions.allowed} onClick={onRequestEditPermissions}>
          Edit permissions&hellip;
        </MenuItem>
      </ActionTooltip>
      <ActionTooltip availability={actions.remove}>
        <ButtonWithDialog
          componentType="MenuItem"
          color="error"
          disabled={isBusy || !actions.remove.allowed}
          dialogTitle={`Remove ${member.user_email} from this workspace?`}
          dialogContent={`Are you sure you want to remove ${member.user_email} from this workspace?`}
          confirmButtonText="Remove"
          confirmButtonProps={{ color: 'error' }}
          onClick={() => removeMember(member.user_id)}
        >
          Remove&hellip;
        </ButtonWithDialog>
      </ActionTooltip>
    </Dropdown>
  );
};
